import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreateOrPutCustomerInput, Customer } from '@thro-types/billing';

import { AxiosRequestConfig } from 'axios';

import {
  createCustomer,
  updateCustomer,
  getCustomer,
  getCustomers,
  UpdateInterface,
  getCustomerWithInvoices,
  importCustomers,
  updateInvoiceCustomer
} from '../../billingService';

export const useGetCustomersListQuery = (enabled: boolean, config?: AxiosRequestConfig) => {
  const queryKey = ['customers', config ? JSON.stringify(config.params) : ''];
  return useQuery({
    queryKey,
    queryFn: () => {
      return getCustomers(config);
    },
    placeholderData: keepPreviousData,
    enabled
  });
};

export const useGetCustomerQuery = (enabled = true, id: string, config?: AxiosRequestConfig) => {
  const queryKey = ['customers', id, config ? JSON.stringify(config.params) : ''];
  return useQuery({
    queryKey,
    queryFn: () => {
      return getCustomer(id, config);
    },
    enabled
  });
};

export const useGetCustomerWithInvoicesQuery = (id: string, config?: AxiosRequestConfig) => {
  const queryKey = ['customers', id, config ? JSON.stringify(config.params) : ''];
  return useQuery({
    queryKey,
    queryFn: () => {
      return getCustomerWithInvoices(id, config);
    },
    placeholderData: keepPreviousData
  });
};

export const useInvoiceCustomerMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();

  const queryKey = ['invoices'];

  const update = useMutation<Customer, Error, { id: string; data: CreateOrPutCustomerInput }>({
    mutationFn: async args => {
      return await updateInvoiceCustomer(args.id, args.data, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });
  return { update } as const;
};

export const useCustomerMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();

  const queryKey = ['customers'];

  const create = useMutation<Customer, Error, CreateOrPutCustomerInput>({
    mutationFn: async args => {
      return await createCustomer(args, config);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {}
  });

  const update = useMutation<Customer, Error, UpdateInterface<CreateOrPutCustomerInput>>({
    mutationFn: async args => {
      return await updateCustomer(args, config);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {}
  });
  return { create, update } as const;
};

export const useCustomerImportMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();
  const queryKey = ['customers'];
  const create = useMutation<Customer[], Error, FormData>({
    mutationFn: async args => {
      return await importCustomers(args, config);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {}
  });
  return { create } as const;
};
